// LEGACY HISTORY'S PIECES (UI rebuild batch 5). The row's words (kindWord, the instruction, the reconcile verdict) and
// the Mark form are COPIED from screens/Migration.tsx — what the screen reads and writes is today's; the form now lives
// in the record's detail view, behind "Mark…", instead of under each row.
import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { MigrationRow, MigrationStatus } from '../../../lib/api';
import { displayAsset, fmtAmount, fmtRate } from '../../../lib/format';
import { partyName, shortReference } from '../../../lib/naming';
import { Amount } from '../../Ledger';
import { Field } from '../../Field';
import { Refused } from '../../OnboardingParts';
import { Pill } from '../../Pill';
import { StateLine } from '../kit';
import { groupedFigure } from '../../../screens/Migration';

/** The record's kind in a word — Sell / Buy for a trade, the legacy entry's own type otherwise. The raw kind ("txn" /
 *  "pri") never reaches the screen. (Migration.tsx kindWord, verbatim.) */
export function kindWord(r: MigrationRow): string {
  if (r.kind === 'txn') return r.tran_from?.startsWith('sale') ? 'Sell' : 'Buy';
  const t = r.tran_type ?? '';
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

/** What the record says happened, in full — quantity, rate and total for a trade; the amount and who to whom
 *  otherwise. (Migration.tsx Instruction.) */
export function Instruction({ r }: { r: MigrationRow }) {
  if (r.kind === 'txn') {
    return (
      <span>
        <Amount value={r.quantity ?? ''} asset={r.asset ?? ''} />{' '}
        <span className="amt whitespace-nowrap text-tg-hint">@ {fmtRate(r.rate, r.quote_ccy ?? undefined)}</span>{' '}
        <span className="text-tg-hint">=</span>{' '}
        <Amount value={r.total ?? ''} asset={r.quote_ccy ?? ''} />
      </span>
    );
  }
  return (
    <span>
      <Amount value={r.quantity ?? ''} asset={r.asset ?? ''} />
      {r.party && r.counterparty && (
        <span className="text-tg-hint">
          {' '}· {partyName({ display_name: r.party })} <ArrowRight size={11} className="inline" /> {partyName({ display_name: r.counterparty })}
        </span>
      )}
    </span>
  );
}

/** The record's state in a word. An entered record with its live deal reads "Linked" (Astra's word); one entered with no
 *  deal named, "Entered". */
export function StateWord({ r }: { r: MigrationRow }) {
  if (r.status === 'entered') return <Pill tone="good">{r.entered_deal_id ? 'Linked' : 'Entered'}</Pill>;
  if (r.status === 'skipped') return <Pill>Skipped</Pill>;
  if (r.status === 'blocked') return <Pill tone="warn">Blocked</Pill>;
  return <Pill>Not entered</Pill>;
}

/** The reconcile check's verdict after this entry, as scripts/migration-reconcile.mjs recorded it: one line, and the
 *  balances that disagree (first four) behind Details. (Migration.tsx DriftNote, lighter.) */
export function DriftNote({ r }: { r: MigrationRow }) {
  const d = r.drift_after;
  if (!d) return null;
  if (!d.drift?.length) {
    return <StateLine tone="good">{d.checked} {d.checked === 1 ? 'balance agrees' : 'balances agree'} with legacy.</StateLine>;
  }
  return (
    <StateLine
      tone="warn"
      more={d.drift.slice(0, 4).map((x) => (
        <p key={`${x.slug}-${x.asset}`}>
          {x.name} {displayAsset(x.asset)} — legacy <span className="amt">{fmtAmount(x.legacy, x.asset)}</span>, books{' '}
          <span className="amt">{fmtAmount(x.actual, x.asset)}</span>
        </p>
      ))}
    >
      {d.drift.length} {d.drift.length === 1 ? 'balance disagrees' : 'balances disagree'} after this entry.
    </StateLine>
  );
}

export interface MarkVars { legacyId: string; status: MigrationStatus; deal_id?: string; reason?: string }

/** MARK A RECORD — the old row's panel, verbatim in what it sends: the deal it was booked as (optional; matching deals
 *  to click, never a booking link — the legacy era closed at the opening count), a reason (required to skip or block),
 *  and Entered / Skip / Block / Cancel. Nothing here books anything. */
export function MarkForm({ r, pending, error, onMark, onCancel }: {
  r: MigrationRow;
  pending: boolean;
  error: unknown;
  onMark: (v: MarkVars) => void;
  onCancel: () => void;
}) {
  const [dealId, setDealId] = useState('');
  const [reason, setReason] = useState('');
  const candidates = r.candidates ?? [];
  return (
    <div className="space-y-4">
      <Field label="Deal it was booked as" hint="optional">
        <input className="input font-mono" placeholder="DEAL-2026-08-21-XXXXXX" value={dealId} onChange={(e) => setDealId(e.target.value)} />
      </Field>
      {candidates.length === 0 ? (
        <p className="text-meta text-tg-hint">No booked deal matches this record.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-meta text-tg-hint">Matching deals — tap to use</p>
          <div className="flex flex-wrap gap-2">
            {candidates.map((cd) => (
              <button
                key={cd.deal_id}
                type="button"
                onClick={() => setDealId(cd.deal_id)}
                title={`${cd.deal_id} · ${cd.direction} · take ${cd.take} · give ${cd.give}`}
                className={`min-h-11 max-w-full rounded-lg border px-3 font-mono text-meta transition-colors lg:min-h-10 ${
                  dealId === cd.deal_id ? 'border-tg-accent bg-tg-accent/[0.14] text-tg-accent' : 'border-stroke bg-panel text-tg-text hover:border-brand-muted'
                }`}
              >
                {shortReference(cd.deal_id)}<span className="ml-2 text-tg-hint">{groupedFigure(cd.give)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <Field label="Reason" hint="to skip or block">
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          className="btn-primary order-last col-span-3 px-3 text-body"
          disabled={pending}
          onClick={() => onMark({ legacyId: r.legacy_id, status: 'entered', deal_id: dealId.trim() || undefined })}
        >
          Entered
        </button>
        <button type="button" className="btn-secondary px-3 text-body" disabled={pending || !reason.trim()}
          onClick={() => onMark({ legacyId: r.legacy_id, status: 'skipped', reason: reason.trim() })}>
          Skip
        </button>
        <button type="button" className="btn-secondary px-3 text-body" disabled={pending || !reason.trim()}
          onClick={() => onMark({ legacyId: r.legacy_id, status: 'blocked', reason: reason.trim() })}>
          Block
        </button>
        <button type="button" className="btn-secondary px-3 text-body" onClick={onCancel}>Cancel</button>
      </div>
      {error != null && <Refused error={error} />}
    </div>
  );
}
